// src/components/pages/Movimento/CreateOrEdit/tabs/FreteTabPanel.js
import React, { useMemo } from 'react';
import { ThemeProvider, Button, Box, Tooltip, IconButton } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import Select from '../../../../../form/Select';
import DataList from '../../../../../form/DataList';
import InputNumber from '../../../../../form/InputNumber';
import Checkbox from '../../../../../form/Checkbox';
import TextArea from '../../../../../form/TextArea';

import { formatarNumero } from '../../../../../../utils/masks';

export default function FreteTabPanel({
    // UI/Tema
    theme,

    module,
    // contexto
    operacao,
    casaDecimal = 2,

    // enums/options
    enumFreteTipo,
    enumFreteResponsavelFinanceiro,

    OptionsForma,
    OptionsPrazo,
    OptionsConta,
    OptionsCentroCusto,

    // permissões
    podeEditarItem,
    podeCancelarItem,
}) {
    const { main, draft, list, mrt, flags, actions, state } = module || {};

    const novoFrete = draft?.value || {};
    const data = list?.items || [];
    const rowCount = list?.rowCount || 0;

    const Transportadora = state?.Transportadora || {};
    const OptionsTransportador = state?.OptionsTransportador || [];

    const canSave = flags?.canSave ?? state?.canSave ?? false;
    const isSaving = flags.isSaving ?? false;

    const {
        change: onChange = () => {},
        focus: onFocus = async () => {},
        blur: onBlur = async () => {},
        clear: onLimpar = () => {},
        submitDraft: onSalvar = async () => {},
        selectRow: onRowAction = () => {},
    } = actions || {};

    const {
        state: {
            columnFilterFns = {},
            columnFilters = [],
            globalFilter = '',
            pagination = { pageIndex: 0, pageSize: 5 },
            sorting = [],
        } = {},
        setters: {
            setColumnFilters = () => {},
            setColumnFilterFnsMode = () => {},
            setGlobalFilter = () => {},
            setPagination = () => {},
            setSorting = () => {},
        } = {},
        flags: { isLoading = false, isRefetching = false, isError = false } = {},
    } = mrt || {};

    const manual = !!main?.state?.id;

    const columns = useMemo(
        () => [
            {
                id: 'frete',
                header: `Valores de frete no movimento ${operacao?.descricao ?? ''} - ${operacao?.sigla ?? ''}`,
                columns: [
                    {
                        accessorKey: 'Transportadora.descricao',
                        id: 'Transportadora.descricao',
                        header: 'Transportadora',
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                    },
                    {
                        accessorKey: 'tipoFrete',
                        header: 'Tipo',
                        filterFn: 'equals',
                        columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                        filterVariant: 'select',
                        filterSelectOptions: enumFreteTipo,
                    },
                    {
                        accessorKey: 'responsavelFinanceiro',
                        header: 'Resp. Financeiro',
                        filterFn: 'equals',
                        columnFilterModeOptions: ['fuzzy', 'equals', 'notEquals'],
                        filterVariant: 'select',
                        filterSelectOptions: enumFreteResponsavelFinanceiro,
                    },
                    {
                        accessorKey: 'valorTotal',
                        header: 'Valor Total',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            !row.valorTotal
                                ? ''
                                : parseFloat(row.valorTotal)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: casaDecimal,
                                      maximumFractionDigits: casaDecimal,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'provisao',
                        header: 'Provisão',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: 1 },
                            { label: 'Não', value: 0 },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.provisao;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                ],
            },
        ],
        [operacao, enumFreteTipo, enumFreteResponsavelFinanceiro, casaDecimal]
    );

    return (
        <div style={{ margin: '1em' }}>
            {/* Linha 1 */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                }}
            >
                <Select
                    name="novoFrete.tipoFrete"
                    title="Informa o tipo do frete CIF/FOB"
                    text="Tipo"
                    inputWidth="150px"
                    options={enumFreteTipo}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.tipoFrete ?? ''}
                    isEnum={true}
                    notOptNull={true}
                />

                <Select
                    name="novoFrete.responsavelFinanceiro"
                    title="Informa o responsável financeiro pelo lançamento do frete"
                    text="Responsável"
                    inputWidth="150px"
                    options={enumFreteResponsavelFinanceiro}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.responsavelFinanceiro ?? ''}
                    isEnum={true}
                    notOptNull={true}
                />

                <DataList
                    text={'Transportadora'}
                    name="Transportadora.descricao"
                    placeholder={'Pesquise o transportador'}
                    title={`digite para pesquisar o Transportadora da carga`}
                    inputWidth="350px"
                    filteredOptions={OptionsTransportador}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={Transportadora?.descricao || ''}
                />

                <Select
                    name="novoFrete.formaId"
                    title={'Escolha a forma de pagamento ou recebimento do frete'}
                    text="Forma"
                    inputWidth="150px"
                    options={OptionsForma}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.formaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Select
                    name="novoFrete.prazoId"
                    title={'Define o prazo padrão para o lançamento'}
                    text="Prazo"
                    inputWidth="150px"
                    options={OptionsPrazo}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.prazoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Select
                    name="novoFrete.contaId"
                    title={'Define a conta padrão para o lançamento'}
                    text="Conta"
                    inputWidth="150px"
                    options={OptionsConta}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.contaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Select
                    name="novoFrete.centroCustoId"
                    title={'Define o centro de custo padrão para o lançamento'}
                    text="Centro Custo"
                    inputWidth="200px"
                    options={OptionsCentroCusto}
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.centroCustoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                <Checkbox
                    title="provisiona o valor no financeiro"
                    text="Provisão"
                    name="novoFrete.provisao"
                    handleOnChange={onChange}
                    value={novoFrete?.provisao}
                    checked={!!novoFrete?.provisao}
                />
            </div>

            {/* Linha 2 */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: '8px',
                }}
            >
                <InputNumber
                    text={'Frete'}
                    type="text"
                    name="novoFrete.valorFrete"
                    placeholder="R$"
                    inputWidth="100px"
                    title="valor do frete"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorFrete ? formatarNumero(novoFrete.valorFrete) : ''}
                />
                <InputNumber
                    text={'Seguro'}
                    type="text"
                    name="novoFrete.valorSeguro"
                    placeholder="R$"
                    inputWidth="100px"
                    title="valor do seguro"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorSeguro ? formatarNumero(novoFrete.valorSeguro) : ''}
                />
                <InputNumber
                    text={'Pedágio'}
                    type="text"
                    name="novoFrete.valorPedagio"
                    placeholder="R$"
                    inputWidth="100px"
                    title="valor do pedágio"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorPedagio ? formatarNumero(novoFrete.valorPedagio) : ''}
                />
                <InputNumber
                    text={'Alimentação'}
                    type="text"
                    name="novoFrete.valorAlimentacao"
                    placeholder="R$"
                    inputWidth="110px"
                    title="despesas alimentação do motorista"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorAlimentacao ? formatarNumero(novoFrete.valorAlimentacao) : ''}
                />
                <InputNumber
                    text={'Outros'}
                    type="text"
                    name="novoFrete.valorOutros"
                    placeholder="R$"
                    inputWidth="100px"
                    title="outros valores"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorOutros ? formatarNumero(novoFrete.valorOutros) : ''}
                />
                <InputNumber
                    text={'Total'}
                    type="text"
                    name="novoFrete.valorTotal"
                    placeholder="Total R$"
                    inputWidth="100px"
                    title="valor total"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.valorTotal ? formatarNumero(novoFrete.valorTotal) : ''}
                    readonly={true}
                />

                <Button
                    variant="contained"
                    color="inherit"
                    onClick={onLimpar}
                    style={{ marginLeft: '10px', minWidth: '80px', fontSize: '14px' }}
                >
                    Limpar
                </Button>

                <Button
                    variant="contained"
                    color={(novoFrete?.funcao || '')?.toLowerCase() === 'cancelar' ? 'error' : 'info'}
                    onClick={onSalvar}
                    disabled={!canSave || isSaving || !(novoFrete?.valorTotal && Number(novoFrete.valorTotal) > 0)}
                    style={{ marginLeft: '10px', minWidth: '80px', fontSize: '14px' }}
                >
                    {isSaving ? 'Processando...' : novoFrete?.funcao || 'Adicionar'}
                </Button>
            </div>

            {/* Obs */}
            <div style={{ marginTop: '8px' }}>
                <TextArea
                    text={`Observações do frete`}
                    type="text"
                    name="novoFrete.obs"
                    placeholder="Digite aqui as observações"
                    title="Digite as observações"
                    handleOnChange={onChange}
                    handleOnFocus={onFocus}
                    handleOnBlur={onBlur}
                    value={novoFrete?.obs || ''}
                    textAlign="left"
                    linhas="2"
                    colunas="80"
                />
            </div>

            {/* GRID */}
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    columns={columns}
                    data={data || []}
                    rowCount={rowCount || 0}
                    getRowId={(row) => row.uuid ?? row.id}
                    manualPagination={!!manual}
                    manualSorting={!!manual}
                    manualFiltering={!!manual}
                    enableColumnFilterModes
                    enableGlobalFilterModes
                    enableColumnOrdering
                    enableEditing
                    localization={MRT_Localization_PT_BR}
                    initialState={{ showColumnFilters: true, density: 'compact' }}
                    muiToolbarAlertBannerProps={isError ? { color: 'error', children: 'Error loading data' } : undefined}
                    onColumnFiltersChange={setColumnFilters}
                    onColumnFilterFnsChange={setColumnFilterFnsMode}
                    onGlobalFilterChange={setGlobalFilter}
                    onPaginationChange={setPagination}
                    onSortingChange={setSorting}
                    state={{
                        columnFilterFns,
                        columnFilters,
                        globalFilter,
                        isLoading,
                        pagination,
                        showAlertBanner: isError,
                        showProgressBars: isRefetching,
                        sorting,
                    }}
                    positionExpandColumn="first"
                    positionActionsColumn="first"
                    positionToolbarAlertBanner="bottom"
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                            <Tooltip arrow placement="top" title="Editar">
                                <span>
                                    <IconButton
                                        onClick={() => onRowAction?.(row.original, row.index, 'Editar')}
                                        disabled={!podeEditarItem}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip arrow placement="right" title="Cancelar">
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => onRowAction?.(row.original, row.index, 'Cancelar')}
                                        disabled={!podeCancelarItem}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>
        </div>
    );
}
